import axios from "axios";
import * as Constants from "../../constants"
export interface Warning {
    polygons: {lat:number, lng: number}[][];
    url: string;
    color: string;
    fillColor: string;
    expires: string;
    type: string;
	weight: number;
}

export interface ParsedWarningContent {
    title: string;
    date: string;
    issuer: string;
    areas: string;
    what: string;
    where: string | null;
    when?: string;
    impact: string;
    actions: string;
    isFallback: boolean;
    fallbackContent?: string;
}

export type ParsedWarningContentWithoutFallback = Omit<ParsedWarningContent, 'isFallback' | 'fallbackContent'>;


export enum OtherWarningPattern {
    AdvisoryAlert = "AdvisoryAlert",
    ForecastSynopsis = "ForecastSynopsis",
    Flood = "Flood",
    FlashFlood = "FlashFlood",
    Fallback = "Fallback"
}

// if (hasAsteriskSections) return "PatternAsterik";
// return "PatternNoAsterik";

export enum SevereWarningPattern {
    PatternAsterik = "PatternAsterik",
    PatternNoAsterik = "PatternNoAsterik"
}

export class SevereWarnings {
    severeWarnings: Warning[];
    showTornadoWarnings:boolean;
    tornadoColor:string = '255 255 0';
    severeThunderstormColor:string = '219 112 147';
	specialMarineColor:string = '255 255 255';
    showSevereThunderstormWarnings:boolean = false;
	hideSpecialMarines:boolean = false;
	highlightCriticalWarnings:boolean = false;
    
    constructor(showTornadoWarnings: boolean, showSevereThunderstormWarnings: boolean, hideSpecialMarines: boolean, highlightCriticalWarnings: boolean) {
        this.severeWarnings = [];
        this.showTornadoWarnings = showTornadoWarnings;
        this.showSevereThunderstormWarnings = showSevereThunderstormWarnings;
		this.hideSpecialMarines = hideSpecialMarines;
		this.highlightCriticalWarnings = highlightCriticalWarnings;
    }

    addWarning(warning: Warning) {
        this.severeWarnings.push(warning);
    }
    
    async getDataWarnings(url: string) {
        try {
            const response = await axios.get(Constants.WARNINGS_AND_ALERTS_INFO_API_URL ?? '',{
                params: {
                    url,
                    isBulletin: 1
                }
            });
            return response.data;
        } catch (error) {
            console.error(error);
            return '';
        }
        
    }

    formatArrayToString(array: string[]): string {
        if(array.length === 0) return '';
        if(array.length === 1) return array[0];

        const lastElement = array.pop();
        const secondLastElement = array.pop();

        return `${array.join(', ')}, ${secondLastElement} and ${lastElement}.`;
    }

    determineOtherWarningPattern(text: string): OtherWarningPattern {

        const hasAsteriskSections = text.includes('* WHAT...') || text.includes('* WHERE...');

        const hasSynopsis = text.includes('.SYNOPSIS');
        const hasFloodWarnings = text.includes('Flood');
        const isFlashFlood = text.includes('Flash Flood Warning');
        if(hasAsteriskSections) return OtherWarningPattern.AdvisoryAlert;
        if(hasSynopsis) return OtherWarningPattern.ForecastSynopsis;
        if(isFlashFlood) return OtherWarningPattern.FlashFlood;
        if(hasFloodWarnings) return OtherWarningPattern.Flood;
        return OtherWarningPattern.Fallback;
    }

    determineSevereWarningPattern(text: string): SevereWarningPattern {
        const hasAsteriskSections = text.includes('* At') || text.includes('* Some') || text.includes('* Until');

        if (hasAsteriskSections) return SevereWarningPattern.PatternAsterik;
        return SevereWarningPattern.PatternNoAsterik;
    }

    cleanMatch(match: RegExpMatchArray | null): string {
        return match?.[1]?.replace(/\.\.\./g, " ").replace(/\*/g, '').replace(/\s+in[^,]+/g, '').trim() || "";
    }

    async getWarningsContent(type: string ,url: string){
        const response = await axios.get(Constants.WARNINGS_AND_ALERTS_INFO_API_URL ?? '',{
            params: {
                url,
                isBulletin: 1
            }
        });

        const contentData = response.data;
        let parsedData : ParsedWarningContent = {
            actions: "",
            areas: "",
            date: "",
            impact: "",
            issuer: "",
            title: "",
            what: "",
            where: "",
            when: "",
            isFallback: false
        };

        if(type === 'normal_warning'){
            switch (this.determineOtherWarningPattern(contentData)) {
                case OtherWarningPattern.AdvisoryAlert:
                    parsedData = this.getParseAdvisoryAlertWarnings(contentData);
                    return parsedData;
                case OtherWarningPattern.ForecastSynopsis:
                    parsedData = this.getParseSynopsisForecast(contentData);
                    return parsedData;
                case OtherWarningPattern.Flood:
                    parsedData = this.getParseFloodWarning(contentData);
                    return parsedData;
                case OtherWarningPattern.FlashFlood:
                    parsedData = this.getParseFlashFloodWarning(contentData);
                    return parsedData;
                case OtherWarningPattern.Fallback:
                    parsedData = this.getFallContent(contentData);
                    return parsedData;
            }
        } else{
            switch (this.determineSevereWarningPattern(contentData)) {
                case SevereWarningPattern.PatternAsterik:
                    parsedData = this.getParsedSevereWarnings(contentData);
                    return parsedData;
                case SevereWarningPattern.PatternNoAsterik:
                    parsedData = this.getParsedNoAsterikSevereWarnings(contentData);
                    return parsedData;        
            }
        }
    }

    getParsedNoAsterikSevereWarnings(warnings: string): ParsedWarningContent {
        const headerMatch: RegExpMatchArray | null = warnings.match(/<p[^>]*>(.*?)<\/p>/);
        const headerExtractedText: string | null = headerMatch ? headerMatch[1] : null;
        const [titleMatch, headerRemaining]: string[] = (headerExtractedText && headerExtractedText.split("issued")) || ["", ""];
        const title: string = (titleMatch && titleMatch.trim()) || '';
        const [, sourceMatch]: string[] = headerRemaining ? headerRemaining.split("by") : ['', ''];
        const [sourceTitle, place,]: string[] = sourceMatch ? sourceMatch.trim().split(" ") : ['', ''];
        const issuer: string = sourceTitle ? `${sourceTitle === "NWS" ? "National Weather Service," : sourceTitle} ${place}`: '';

        const cleanText: string = warnings.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        const dateTimeMatch: RegExpMatchArray | null = cleanText.match(/(\d{3,4} [AP]M [CE]DT) ([A-Za-z]+ [A-Za-z]+ \d{1,2} \d{4})/);

        let date = '';
        if (dateTimeMatch) {
            date = dateTimeMatch[2] || '';
        }
        
        const remainingContent: string = cleanText.replace(/<p[^>]*>.*?<\/p>\s*<br\/?>/, '') || '';
        
        const what: string = remainingContent.split("HAZARD...")[0].trim() || '';

        const impactMatch: RegExpMatchArray | null = cleanText.match(/IMPACT\.\.\.([^]*?\.)/);
        
        let impact: string = '';
        if (impactMatch){
            impact = impactMatch[1] ? impactMatch[1].trim() : '';
        }

        const locationMatch: RegExpMatchArray | null = cleanText.match(/include\.\.\.([^]*?\.)/);
        
        let where: string = '';
        if (locationMatch) {
            where = locationMatch[1] ? locationMatch[1].trim() : '';
        }

        const result =  {
            title,
            date,
            issuer,
            areas: "",
            what,
            where,
            when: "",
            impact,
            actions: "",
            isFallback: false
        };
        return result;
    }

    getParsedSevereWarnings(warnings: string): ParsedWarningContent  {

        const parsedContent: ParsedWarningContentWithoutFallback = {
            title: "",
            date: "",
            issuer: "",
            areas: "",
            what: "",
            where: "",
            when: "",
            impact: "",
            actions: ""
        };

        const cleanText: string = warnings.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();

        const dateTimeMatch = cleanText.match(/(\d{3,4} [AP]M [CE]DT) ([A-Za-z]+ [A-Za-z]+ \d{1,2} \d{4})/);

        if (dateTimeMatch) {
            parsedContent.date = (dateTimeMatch[2] && dateTimeMatch[2]) || '';
        }


        const issuerMatch = cleanText.match(/The ([^]*?) in ([^]*?) has issued/);
        parsedContent.issuer = (issuerMatch && issuerMatch.length >= 3 )
            ? `${issuerMatch[1].trim()}, ${issuerMatch[2].trim()}` 
            : '';
        
        const titleMatch = cleanText.match(/\* ([^]*?) for\.\.\. ([^]*?)\* Until/);
        if (titleMatch) {
            parsedContent.title = titleMatch[1] || '';
            
            const areasText = titleMatch[2] || '';
            const areasList: string[]  = areasText
                .split('...')
                .map((area:string) => area.trim())
                .filter((area: string) => area && !area.includes('*'));
            
            parsedContent.areas = areasList.join(', ');
        }
                
        const atTimeMatch = cleanText.match(/At ([^]*?(?=HAZARD|$))/);
        if (atTimeMatch) {
            parsedContent.what = atTimeMatch[0].trim() || '';
        }
        
        const impactMatch = cleanText.match(/IMPACT\.\.\. ([^]*?)(?=\*|$)/);
        if (impactMatch) {
            parsedContent.impact = impactMatch[1].trim() || '';
        }
        
        const locationsMatch = cleanText.match(/Locations impacted include\.\.\. ([^]*?)(?=PRECAUTIONARY|$)/);
        const locations = locationsMatch
            ? locationsMatch[1]
                .split('...')
                .map((loc: string) => loc.trim())
                .filter(Boolean)
            : [];

        parsedContent.where = this.formatArrayToString(locations);
        
        const actionsMatch = cleanText.match(/PRECAUTIONARY\/PREPAREDNESS ACTIONS\.\.\. ([^]*?)$/);

        if (actionsMatch) {
            parsedContent.actions = (actionsMatch[0]
                .split("<br/><br/><br/>")[0]
                .split("...")[1]
                .trim()) || '';
        }

        const result: ParsedWarningContent = {
            ...parsedContent,
            isFallback: false
        }

        return result;
    }

    getParseFlashFloodWarning(warning: string): ParsedWarningContent {
        const parsedContent: ParsedWarningContentWithoutFallback = {
            title: "",
            date: "",
            issuer: "",
            areas: "",
            what: "",
            where: "",
            when: "",
            impact: "",
            actions: ""
        };
    
        try {
            const parser = new DOMParser();
            const doc = parser.parseFromString(warning, "text/html");
            parsedContent.title = doc.querySelector("p")?.textContent?.trim() || "";
    
            let cleanText = warning.replace(/<p[^>]*>.*?<\/p>/, '').trim();
            
            const [mainContent, actionsContent] = cleanText.split('PRECAUTIONARY/PREPAREDNESS ACTIONS');

            const issuerMatch = cleanText.match(/The ([^]*?) in ([^]*?) has issued/);
            parsedContent.issuer = (issuerMatch && issuerMatch.length >= 3) ? `${issuerMatch[1].trim()}, ${issuerMatch[2].trim()}` : '';

            const dateTimeMatch = cleanText.match(/(\d{3,4} [AP]M [CE][DS]T) ([A-Za-z]+ [A-Za-z]+ \d{1,2} \d{4})/);

            if (dateTimeMatch) {
                parsedContent.date = (dateTimeMatch[2] && dateTimeMatch[2]) || '';
            }
            
            if (mainContent) {
                parsedContent.areas = this.cleanMatch(mainContent.match(/\* ([^*]+County[^*]+) \*/));                
                
                parsedContent.when = this.cleanMatch(mainContent.match(/\* Until ([^*]+)\./));
                
                parsedContent.what = this.cleanMatch(mainContent.match(/\* At [^*]+shortly\./));                
                

                const impactMatch = mainContent.match(/IMPACT\.\.\. ([^*]+?)(?=\*|PRECAUTIONARY|$)/);
                if (impactMatch) {
                    parsedContent.impact = (impactMatch[1] && impactMatch[1].trim()) || '';
                }
                
                const whereMatch = mainContent.match(/\* Some locations that will experience flash flooding include\.\.\. ([^*]+?)(?=\*|PRECAUTIONARY|$)/);
                parsedContent.where = this.cleanMatch(whereMatch);
            }
            
            if (actionsContent) {
                parsedContent.actions = actionsContent
                    .split('<br/>')
                    .map(line => line.trim())
                    .filter(line => line && !line.includes('&&'))
                    .join(' ')
                    .replace(/\.\.\./g, '')
                    .trim();
            }
            
            (Object.keys(parsedContent) as Array<keyof ParsedWarningContentWithoutFallback>).forEach((key) => {
                if (typeof parsedContent[key] === 'string') {
                    parsedContent[key] = (parsedContent[key] as string)
                        .replace(/&nbsp;/g, ' ')
                        .replace(/\s+/g, ' ')
                        .replace(/\+/g, ' ')
                        .trim();
                }
            });
            
        } catch (error) {
            console.error('Error parsing flash flood warning:', error);
        }
        const result: ParsedWarningContent = {
            ...parsedContent,
            isFallback: false,
        }
        
        return result;
    }

    getParseFloodWarning(warning: string): ParsedWarningContent {
        const parsedContent: ParsedWarningContentWithoutFallback = {
            title: "",
            date: "",
            issuer: "",
            areas: "",
            what: "",
            where: "",
            when: "",
            impact: "",
            actions: ""
        };

        try {
            const cleanText: string = warning.replace(/<p[^>]*>.*?<\/p>/, '').trim();
            
            const [mainContent, actionsContent] = cleanText.split('PRECAUTIONARY/PREPAREDNESS ACTIONS...');
            
            const mainSections = (mainContent && mainContent.split('<br/><br/>')) || '';

            const headerSection = (mainSections && mainSections[0].replace(/<br\/>/g, ' ').trim()) || '';
            const issuerMatch:RegExpMatchArray | null = headerSection.match(/The (National Weather Service.*?) has issued a (.*?) for/);
            if (issuerMatch) {
                parsedContent.issuer = issuerMatch[1].trim() || '';
                parsedContent.title = issuerMatch[2].trim() || '';
            }

            let affectedAreas: string[] = [];
            let forecastInfo: string[] = [];
            let causationInfo: string[] = [];

            for (let i = 1; i < mainSections.length; i++) {
                const section = mainSections[i].replace(/<br\/>/g, ' ').trim();
                
                if (!section) continue;

                if (section.includes('affecting')) {
                    affectedAreas.push(section);
                } else if (section.startsWith('.')) {
                    causationInfo.push(section.substring(1).trim());
                } else if (section.includes('...including') && section.includes('flooding is forecast')) {
                    forecastInfo.push(section);
                } else if (!section.includes('weather.gov') && !section.includes('next statement')) {
                    if (!section.includes('Additional information')) {
                        affectedAreas.push(section);
                    }
                }
            }

            if (affectedAreas.length > 0) {
                parsedContent.where = affectedAreas.join('. ').replace(/\.\./g, '.').trim() || '';
            }

            let whatParts: string[] = [];
            if (causationInfo.length > 0) {
                whatParts.push(causationInfo.join(' '));
            }
            if (forecastInfo.length > 0) {
                whatParts.push(forecastInfo.join(' '));
            }
            parsedContent.what = whatParts.join(' ').replace(/\.\.\./g, ' ').trim() || '';

            if (actionsContent) {
                const actionLines = actionsContent
                    .split('<br/><br/>')
                    .map(line => line.replace(/<br\/>/g, ' ').trim() || '')
                    .filter(line => line.length > 0)
                    .join(' ');
                
                parsedContent.actions = actionLines;
            }

            (Object.keys(parsedContent) as Array<keyof ParsedWarningContentWithoutFallback>).forEach((key) => {
                if (typeof parsedContent[key] === 'string') {
                    parsedContent[key] = (parsedContent[key] as string)
                        .replace(/&nbsp;/g, ' ')
                        .replace(/\s+/g, ' ')
                        .replace(/\+/g, ' ') 
                        .trim();
                }
            });

        } catch (error) {
            console.error('Error parsing flood warning:', error);
        }

        const result: ParsedWarningContent = {
            ...parsedContent,
            isFallback: false,
        };

        return result;
    }

    getParseSynopsisForecast(warning: string): ParsedWarningContent {
        const normalizedWarning: string = warning.trim();
        const sections: string[] = normalizedWarning.split("<br/><br/>");
        
        const parsedContent: ParsedWarningContentWithoutFallback = {
            title: "",
            date: "",
            issuer: "",
            areas: "",
            what: "",
            where: "",
            when: "",
            impact: "",
            actions: ""
        };
        
        try {
            const headerSection: string = (sections[0] && sections[0].replace(/.*<\/p>/, '')) || '';
            const headerParts: string[] =  (headerSection && headerSection.split('<br/>')) || [];
            parsedContent.issuer = (headerParts[0] && headerParts[0].trim()) || '';
            
            const dateMatch: RegExpMatchArray | null = (headerParts[1].match(/(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}\s+\d{4})/i)) ;
            parsedContent.date = dateMatch ? dateMatch[1] : '';
            
            if (sections[1]) {
                const titleSection: string = (sections[1] && sections[1].replace(/<br\/>/g, ' ').trim()) || '';
                if (titleSection.includes('for')) {
                    const splitTile = titleSection.split(' for ');
                    parsedContent.title = (splitTile[0] && splitTile[0].trim()) || '';
                    parsedContent.areas = (splitTile[1] && splitTile[1].trim()) || '';
                } else {
                    parsedContent.title = titleSection;
                }
            }
            
            if (sections[2]) {
                parsedContent.what = sections[2].replace(/<br\/>/g, ' ').trim() || '';
            }
            
            const synopsisIndex = sections.findIndex(section => 
                section.includes('.SYNOPSIS') || section.includes('SYNOPSIS FOR')
            );
            
            if (synopsisIndex !== -1) {
                const synopsisAndAfter: string = (sections.slice(synopsisIndex).join('<br/><br/>')) || '';
                
                const synopsisPattern = /\.SYNOPSIS.*?\.\.\.(?:<br\/>)+(.+?)(?:<br\/><br\/>|$)/;
                const synopsisMatch: RegExpMatchArray | null = synopsisAndAfter.match(synopsisPattern);
                
                if (synopsisMatch) {
                    parsedContent.where = (synopsisMatch[1] && synopsisMatch[1]
                        .replace(/<br\/>/g, ' ')
                        .replace(/\s+/g, ' ')
                        .trim()) || '';
                    
                    const afterSynopsis: string = synopsisAndAfter.split(synopsisMatch[0])[1];
                    if (afterSynopsis) {
                        parsedContent.when = (afterSynopsis
                            .replace(/<br\/>/g, ' ')
                            .replace(/\s+/g, ' ')
                            .trim()) || '';
                    }
                } else {
                    const afterSynopsis: string = (synopsisAndAfter.split(/\.SYNOPSIS.*?\.\.\./)[1]) || '';
                    if (afterSynopsis) {
                        parsedContent.where = (afterSynopsis
                            .replace(/<br\/>/g, ' ')
                            .replace(/\s+/g, ' ')
                            .trim()) || '';
                    }
                }
            } else {
                if (sections[3]) {
                    parsedContent.where = (sections[3].replace(/<br\/>/g, ' ').trim()) || '';
                }
                if (sections[4]) {
                    parsedContent.when = (sections[4].replace(/<br\/>/g, ' ').trim()) || '';
                }
            }
            

            (Object.keys(parsedContent) as Array<keyof ParsedWarningContentWithoutFallback>).forEach((key) => {
                if (typeof parsedContent[key] === 'string') {
                    parsedContent[key] = ((parsedContent[key] as string)
                        .replace(/&nbsp;/g, ' ')
                        .replace(/\s+/g, ' ')
                        .replace(/\+/g, ' ')
                        .trim()) || '';
                }
            });
            
        } catch (error) {
            console.error('Error parsing weather warning:', error);
        }

        const result: ParsedWarningContent = {
            ...parsedContent,
            isFallback: false,
        }

        return result;
    }

    getFallContent(warning: string): ParsedWarningContent {
        
        const result: ParsedWarningContent =  {
            title: "",
            date: "",
            issuer: "",
            areas: "",    
            what: "",
            where: "",
            when: "",
            impact: "",
            actions: "",
            isFallback: true,
            fallbackContent: warning
        };

        return result;
    }

    getParseOthers(warning: string): ParsedWarningContent {

        let [firstValue, actionsValue] = warning.split("PRECAUTIONARY/PREPAREDNESS ACTIONS...");
        
        let [basicInfo, whereData, whatData] = firstValue.split("<br/><br/>");
        
        let cleanBasicInfo = basicInfo.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        let basicInfoMatch = cleanBasicInfo.match(/ALERTS (.*?) has issued a (.*?) for/);
        const issuer = basicInfoMatch ? basicInfoMatch[1].replace(" in", ",") : "";
        const title = basicInfoMatch ? basicInfoMatch[2] : "";

        const where = whereData.replace(/<br\/>/g, ' ');
        let what = whatData.replace(/<br\/>/g, ' ').trim().replace(/\.\.\./g, " ")

        if (what.charAt(0) === '.') {
            what = what.slice(1);
        }

        let actions: string = "";
        if(actionsValue) {
            actions = actionsValue.replace(/<br\/>/g, ' ').trim().replace("...", " ");
        }
        
        const result =  {
            title,
            date: "",
            issuer,
            areas: "",    
            what,
            where,
            when: "",
            impact: "",
            actions,
            isFallback: false
        };

        return result;
    }

    getParseAdvisoryAlertWarnings(warning: string): ParsedWarningContent {
        const headerMatch = warning.match(/<p class='header'>ALERTS<\/p>([\s\S]*)/);
        if (!headerMatch) {
            return this.getFallContent(warning);
        }

        const content = headerMatch[1];

        const sections = content.split(/<br\/>\s*<br\/>/);
        if (sections.length < 4) {
            return this.getFallContent(warning);
        }

        const issuerDateSection = sections[0];
        const issuerDateParts = issuerDateSection.split(/<br\/>/) || [];
        const issuer = issuerDateParts[0]?.trim() || "";
        
        const dateMatch = issuerDateParts[1]?.match(/(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+((?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}\s+\d{4})/i);
        const date = dateMatch ? dateMatch[1] : '';

        const areasSection = sections[1] || "";
        let areas = "";
        
        if (areasSection) {
            areas = areasSection
                .replace(/<br\/>/g, '\n')
                .split('\n')
                .map(line => line?.replace(/-$/, '').trim() || "")
                .filter(line => line)
                .filter(line => {
                    return line && 
                        !line.match(/^\d+\s+[AP]M\s+[A-Z]{3}\s+(?:Mon|Tue|Wed|Thu|Fri|Sat|Sun)\s+(?:Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\s+\d{1,2}\s+\d{4}$/);
                })
                .join('\n');

            // Remove leading dot if present
            if (areas.charAt(0) === '.') {
                areas = areas.slice(1);
            }
        }
        
        let headline = '';
        let headlineSection = '';
        
        if (sections[2]?.includes("...")) {
            headlineSection = sections[2] || '';
        } else if (sections.length > 3 && sections[3]?.includes("...")) {
            headlineSection = sections[3] || '';
        } else {
            // Default to section 2 if no clear headline marker is found
            headlineSection = sections[2] || '';
        }

        headline = headlineSection
            .replace(/<br\/>/g, ' ')
            .replace(/\s+/g, ' ')
            .trim();

        let title = "";

        const titleMatch = headline.match(/\.\.\.([^\.]+?(?=\.\.\.))/);

        if(titleMatch && titleMatch[1]){
            title = titleMatch[1].trim();
        }
        if (title.includes("FROM")) {
            title = title.split('FROM')[0].trim();
        }

        let what = '', where = '', when = '', impact = '';
        
        for (let i = 3; i < sections.length; i++) {
            const section = sections[i].replace(/<br\/>/g, ' ').replace(/\s+/g, ' ').trim() || '';
            
            if (section.includes('* WHAT...')) {
                what = section.replace('* WHAT...', '').trim();
            } else if (section.includes('* WHERE...')) {
                where = section.replace('* WHERE...', '').trim();
            } else if (section.includes('* WHEN...')) {
                when = section.replace('* WHEN...', '').trim();
            } else if (section.includes('* IMPACTS...') || section.includes('* IMPACT...')) {
                impact = section.replace(/\* IMPACTS?\.\.\./, '').trim();
            }
        }

        let actions = '';
        const actionsIndex = sections.findIndex(section => 
            section.includes('PRECAUTIONARY/PREPAREDNESS ACTIONS...')
        );
        
        if (actionsIndex !== -1) {
            actions = sections.slice(actionsIndex + 1)
                .join(' ')
                .replace(/<br\/>/g, ' ')
                .replace(/\s+/g, ' ')
                .trim();
        }

        const result: ParsedWarningContent = {
            title,
            date,
            issuer,
            areas,
            what,
            where,
            when,
            impact,
            actions,
            isFallback: false
        }
    
        return result;
    }

    async getWarnings(type:string) {
        try{
            this.clearWarnings();
            let mainWarnings: Warning[] = [];
            const response = await axios.get(Constants.WARNINGS_AND_ALERTS_API_URL + (type === 'normal_warning' ? '?iW=1' : '?oW=1') ?? '');
           const xml = response.data;
            const parser = new DOMParser();
            const xmlDoc = parser.parseFromString(xml, 'text/xml');
            const warnings = xmlDoc.getElementsByTagName('bulletins');
            for (let i = 0; i < warnings.length; i++) {
                const warning = warnings[i];
                const alerts = warning.getElementsByTagName('alert')
                for (let j = 0; j < alerts.length; j++) {
                    const alert = alerts[j];

                    const bulletin: Warning = {
                        polygons: [],
                        url: alert.getAttribute('url') ?? '',
                        color: '#9292ff',
                        fillColor: '#9292ff',
                        expires: alert.getAttribute('expires') ?? '',
                        type: '',
						weight: 1
                    };
                    const color = alert.getAttribute('outlineColor');
                    if (color) {
                        const colorArray = color.split(' ');
                        bulletin.color = this.rgbToHex(colorArray[0], colorArray[1], colorArray[2]);
                    }
                    const fillColor = alert.getAttribute('color');
                    if (fillColor) {

                        const fillColorArray : string[] = fillColor.split(' ');
                        bulletin.fillColor = this.rgbToHex(fillColorArray[0], fillColorArray[1], fillColorArray[2]);
						if(type == "warning"){
							if (fillColor === '255 0 0') {
								bulletin.type = 'Tornado';
							}
							if (fillColor === '0 255 0') {
								bulletin.type = 'Flash Flood';
							}
							if (fillColor === '255 255 0') {
								bulletin.type = 'Severe Thunderstorm';
							}
						}
                    }
                    let polygons = null;
                    if (alert.getElementsByTagName('bounds').length > 0) {
                        polygons = alert.getElementsByTagName('bounds')[0].getAttribute('polygons');
                    }
                    if (polygons) {
                        let generalPoints:{lat:number, lng: number}[][] = [];
                        const arrayPolygons = JSON.parse(polygons);
                        for (let k = 0; k < arrayPolygons.length; k++) {
                            let points:{lat:number, lng: number}[] = [];
                            for (let l = 0; l < arrayPolygons[k].length; l++) {
                                points.push({ lat: arrayPolygons[k][l][0], lng: arrayPolygons[k][l][1] });
                            }
                            generalPoints.push(points);
                        }
                        bulletin.polygons = generalPoints;
                    }

                    const isWarning = (alert.getAttribute('warning') === '1');
                    let allowWatchFilter = true;
                    if(this.showTornadoWarnings || this.showSevereThunderstormWarnings){
                        
                        allowWatchFilter = this.showTornadoWarnings && alert.getAttribute('color') == this.tornadoColor;
                        allowWatchFilter = this.showSevereThunderstormWarnings && alert.getAttribute('color') == this.severeThunderstormColor;
                    }
					
					//Add bold outline if the warning is marked as critical and highlightCriticalWarnings is enabled
					const isCritical = (alert.getAttribute('isCritical') === '1');
					if(isCritical && this.highlightCriticalWarnings){
						bulletin.weight = 3;
					}
					
					//Exclude special marines if hideSpecialMarines is enabled
					if(this.hideSpecialMarines && alert.getAttribute('color') == this.specialMarineColor){
						//Do nothing
					}else{
						if(isWarning){
							mainWarnings.push(bulletin);
						}else {
							if(allowWatchFilter) this.addWarning(bulletin);
						}
					}
                }
            }
        }catch (error) {
            console.error(error);
        }
        
        return this.severeWarnings;
    }
    clearWarnings() {
        this.severeWarnings = [];
    }
    
    componentToHex (c: number | string):string {
        if (typeof c === "string") {
            c = parseInt(c);
        }
        const hex = c.toString(16);
        return hex.length == 1 ? "0" + hex : hex;
    };

    rgbToHex(r:string, g:string, b:string) {
        return "#" + this.componentToHex(r) + this.componentToHex(g) + this.componentToHex(b);
    };
}
